import React, { useState, useEffect, useMemo, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
  Button,
  Card,
  CardContent,
  Alert,
  Snackbar,
} from '@mui/material';
import { useCachedData } from '../hooks/useCachedData';
import { useGlobalDenseMode } from '../hooks/useGlobalDenseMode';
import GlobalDenseModeToolbar from '../components/GlobalDenseModeToolbar';
import {
  MUI_NATIVE_TABLE_TOOLBAR_ACTIONS_SX,
} from '../constants/muiNativeTableDenseLayout';
import { api, endpoints } from '../api/config';

const DEFAULT_LOCATIONS = [
  'Flemington L41',
  'L60-Hardeeville_SC',
  'L74-Perris_CA',
];

const ShipTracking = () => {
  const [location, setLocation] = useState('Flemington L41');
  const [locations, setLocations] = useState(DEFAULT_LOCATIONS);
  const { data: locationsData } = useCachedData(endpoints.locations);

  // Ship Tracking states
  const [shipTrackingData, setShipTrackingData] = useState(null);
  const [shipTrackingLoading, setShipTrackingLoading] = useState(false);
  const [shipTrackingError, setShipTrackingError] = useState(null);
  const [shipTrackingSelectedTable, setShipTrackingSelectedTable] = useState(null); // 'noshipconfirm', 'shipconfirm', or null
  const [selectedNoShipConfirmRowIds, setSelectedNoShipConfirmRowIds] = useState([]);
  const [isSubmittingAudit, setIsSubmittingAudit] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const { denseMode, denseModeTogglePending, persistDenseMode } = useGlobalDenseMode();

  // Initialize locations when locationsData is available
  useEffect(() => {
    if (!locationsData) return;

    const list = Array.isArray(locationsData)
      ? locationsData
      : Object.values(locationsData);

    const uniqueLocations = list
      .map((loc) => loc.location || loc.name || loc.id)
      .filter(Boolean);

    if (uniqueLocations.length > 0) {
      const merged = Array.from(new Set([...DEFAULT_LOCATIONS, ...uniqueLocations]));
      setLocations(merged);
    }
  }, [locationsData]);

  // Fetch ship tracking data
  const fetchShipTrackingData = useCallback(async (locToFetch) => {
    const targetLoc = locToFetch || location;
    if (!targetLoc) return;
    setShipTrackingLoading(true);
    setShipTrackingError(null);
    setShipTrackingData(null);

    try {
      const response = await api.get('/ship-confirm-audit', {
        params: {
          location: targetLoc,
        },
      });
      setShipTrackingData(response.data);
    } catch (err) {
      console.error('Error fetching ship tracking data:', err);
      setShipTrackingError(
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Failed to load ship tracking data'
      );
    } finally {
      setShipTrackingLoading(false);
    }
  }, [location]);

  // Initial fetch on mount
  useEffect(() => {
    fetchShipTrackingData(location);
  }, [fetchShipTrackingData, location]);

  const handleShipTrackingLocationChange = (e) => {
    const newLoc = e.target.value;
    setLocation(newLoc);
    setShipTrackingSelectedTable(null);
    fetchShipTrackingData(newLoc);
  };

  const handleShipTrackingCardClick = (tableType) => {
    setShipTrackingSelectedTable(tableType);
  };

  const handleBackToCards = () => {
    setShipTrackingSelectedTable(null);
  };

  const labelForColumn = (key) => {
    if (!key || typeof key !== 'string') return 'Unknown';
    return key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const shipTrackingSelectedRows = useMemo(() => {
    if (!shipTrackingSelectedTable || !shipTrackingData) return [];
    const tableKey =
      shipTrackingSelectedTable === 'noshipconfirm'
        ? 'noshipconfirm_nomoment'
        : 'shipconfirm_nomoment';
    const rows = Array.isArray(shipTrackingData?.[tableKey]) ? shipTrackingData[tableKey] : [];
    return rows.map((row, index) => {
      const rowId = `${tableKey}-${row?.internal_id ?? 'item'}-${row?.tracking_number ?? 'trk'}-${index}`;
      return {
        ...row,
        id: rowId,
      };
    });
  }, [shipTrackingData, shipTrackingSelectedTable]);

  const isNoShipConfirmTable = shipTrackingSelectedTable === 'noshipconfirm';

  const selectedNoShipConfirmRows = useMemo(() => {
    const selectedIdSet = new Set(selectedNoShipConfirmRowIds);
    return shipTrackingSelectedRows.filter((row) => selectedIdSet.has(row.id));
  }, [shipTrackingSelectedRows, selectedNoShipConfirmRowIds]);

  const shipTrackingSelectedColumns = useMemo(() => {
    const orderedKeys = ['internal_id', 'transaction_id', 'item', 'unique_id', 'tracking_number', 'ship_date'];
    const hiddenByTable = {
      noshipconfirm: new Set(['location']),
      shipconfirm: new Set(['location', 'message']),
    };
    const hiddenKeys = hiddenByTable[shipTrackingSelectedTable] || new Set();

    if (!shipTrackingSelectedRows.length) {
      return orderedKeys.map((key) => ({
        accessorKey: key,
        header: labelForColumn(key),
      }));
    }
    const keySet = new Set();
    shipTrackingSelectedRows.forEach((row) => {
      Object.keys(row || {}).forEach((key) => {
        if (key !== 'id' && !hiddenKeys.has(key)) keySet.add(key);
      });
    });
    const keys = Array.from(keySet);
    const ordered = [
      ...orderedKeys.filter((key) => keys.includes(key)),
      ...keys.filter((key) => !orderedKeys.includes(key)),
    ];
    return ordered.map((key) => ({
      accessorKey: key,
      header: labelForColumn(key),
      cell: (info) => {
        const value = info.getValue();
        if (value === null || value === undefined || value === 'null' || value === '') return '';
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
      },
    }));
  }, [shipTrackingSelectedRows, shipTrackingSelectedTable]);

  const handleAuditSubmit = useCallback(async () => {
    if (isSubmittingAudit) return;

    const getRowTrackingNumber = (row) => {
      if (!row) return null;
      const candidate =
        row.tracking_number ??
        row.trackingNumber ??
        row['Tracking Number'] ??
        row['tracking_no'] ??
        row['trackingNo'] ??
        row.tracking;
      if (candidate != null && String(candidate).trim() !== '' && String(candidate).trim() !== 'null') {
        return String(candidate).trim();
      }
      return null;
    };

    let targetRows = selectedNoShipConfirmRows;
    if (!targetRows.length && selectedNoShipConfirmRowIds.length) {
      const idSet = new Set(selectedNoShipConfirmRowIds);
      targetRows = shipTrackingSelectedRows.filter((r) => idSet.has(r.id));
    }

    const trackingNumbers = Array.from(
      new Set(
        targetRows
          .map(getRowTrackingNumber)
          .filter(Boolean)
      )
    );

    if (trackingNumbers.length === 0) {
      setSnackbar({
        open: true,
        message: 'No valid tracking numbers found in the selected rows.',
        severity: 'warning',
      });
      return;
    }

    setIsSubmittingAudit(true);

    try {
      const response = await api.post('/ship-confirm-audit/check', {
        tracking_numbers: trackingNumbers,
      });

      const successMsg =
        response.data?.message ||
        response.data?.detail ||
        `Ship confirm audit check completed successfully for ${trackingNumbers.length} tracking number(s).`;

      setSnackbar({
        open: true,
        message: successMsg,
        severity: 'success',
      });

      // Clear selection
      setSelectedNoShipConfirmRowIds([]);

      // Automatically re-fetch latest Ship Tracking Details from existing API
      await fetchShipTrackingData(location);
    } catch (err) {
      console.error('Error submitting ship confirm audit:', err);
      const errorMsg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Failed to submit ship confirm audit check.';

      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error',
      });
    } finally {
      setIsSubmittingAudit(false);
    }
  }, [
    isSubmittingAudit,
    selectedNoShipConfirmRows,
    selectedNoShipConfirmRowIds,
    shipTrackingSelectedRows,
    fetchShipTrackingData,
    location,
  ]);

  const isSubmitDisabled = selectedNoShipConfirmRowIds.length === 0 || isSubmittingAudit;

  const actionButtons = isNoShipConfirmTable ? (
    <Button
      variant="contained"
      color="primary"
      size="small"
      disabled={isSubmitDisabled}
      onClick={handleAuditSubmit}
      startIcon={isSubmittingAudit ? <CircularProgress size={14} color="inherit" /> : null}
      sx={{
        textTransform: 'none',
        height: 28,
        fontSize: '0.8125rem',
        fontWeight: 600,
        px: 1.5,
      }}
    >
      {isSubmittingAudit ? 'Submitting...' : 'Mark as Done'}
    </Button>
  ) : null;

  const shipTrackingSelectedGrid = useDataGridProTable({
    data: shipTrackingSelectedRows,
    columns: shipTrackingSelectedColumns,
    loading: shipTrackingLoading,
    error: shipTrackingError || null,
    onRefetch: () => fetchShipTrackingData(location),
    onDataRefresh: () => fetchShipTrackingData(location),
    title: `No Movement Report Details - ${shipTrackingSelectedTable || 'table'}`,
    getRowId: (row) => row.id,
    disablePaper: true,
    hideQuickFilter: true,
    searchOnEnter: false,
    initialPageSize: 1000,
    checkboxSelection: isNoShipConfirmTable,
    rowSelectionModel: isNoShipConfirmTable ? selectedNoShipConfirmRowIds : [],
    onRowSelectionModelChange: (newModel) => {
      if (isNoShipConfirmTable) {
        setSelectedNoShipConfirmRowIds(newModel);
      }
    },
    actionButtons,
  });

  return (
    <DashboardLayout>
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          {!shipTrackingSelectedTable && (
            <Box>
              <Typography
                variant="h5"
                component="h1"
                sx={{
                  mb: 1.5,
                  fontWeight: 'bold',
                  color: 'primary.main',
                  fontSize: '1.25rem',
                }}
              >
                No Movement Report
              </Typography>

              {/* Location Info */}
              <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Box sx={{ minWidth: { xs: 180, sm: 220 } }}>
                    <TextField
                      select
                      fullWidth
                      label="Location"
                      value={location}
                      onChange={handleShipTrackingLocationChange}
                      size="small"
                      sx={{
                        '& .MuiInputBase-root': {
                          fontSize: '0.875rem',
                          height: '36px',
                          bgcolor: 'background.paper',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.875rem',
                        },
                      }}
                    >
                      {locations.map((loc) => (
                        <MenuItem key={loc} value={loc} sx={{ fontSize: '0.875rem' }}>
                          {loc}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box sx={{ ...MUI_NATIVE_TABLE_TOOLBAR_ACTIONS_SX, alignItems: 'center' }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => fetchShipTrackingData(location)}
                      disabled={shipTrackingLoading}
                      sx={{ textTransform: 'none', fontSize: '0.875rem' }}
                    >
                      Refresh
                    </Button>
                    <GlobalDenseModeToolbar
                      denseMode={denseMode}
                      denseModeTogglePending={denseModeTogglePending}
                      persistDenseMode={persistDenseMode}
                    />
                  </Box>
                </Box>
              </Paper>
            </Box>
          )}

          {shipTrackingLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <CircularProgress />
            </Box>
          ) : shipTrackingError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {shipTrackingError}
            </Alert>
          ) : shipTrackingData ? (
            shipTrackingSelectedTable ? (
              <Paper elevation={2} sx={{ p: 0, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, p: 1, px: 1.5 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleBackToCards}
                    sx={{ textTransform: 'none' }}
                  >
                    ← Back to Cards
                  </Button>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {shipTrackingSelectedTable === 'noshipconfirm'
                      ? 'No Ship Confirm - No Movement'
                      : 'Ship Confirm - No Movement'}
                  </Typography>
                </Box>
                <Box sx={{ px: 1, pb: 1, minHeight: 0, flex: 1 }}>
                  <MuiXDataGridSurface hook={shipTrackingSelectedGrid} DataGridPro={DataGridPro} />
                </Box>
              </Paper>
            ) : (
              // Show cards
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {/* No Ship Confirm No Movement Card */}
                <Card
                  elevation={3}
                  sx={{
                    flex: '1 1 300px',
                    minWidth: 300,
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-4px)',
                      transition: 'all 0.2s ease',
                    },
                  }}
                  onClick={() => handleShipTrackingCardClick('noshipconfirm')}
                >
                  <CardContent>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                      No Ship Confirm - No Movement
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {shipTrackingData?.counts?.noshipconfirm_nomoment || 0}
                    </Typography>
                  </CardContent>
                </Card>

                {/* Ship Confirm No Movement Card */}
                <Card
                  elevation={3}
                  sx={{
                    flex: '1 1 300px',
                    minWidth: 300,
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-4px)',
                      transition: 'all 0.2s ease',
                    },
                  }}
                  onClick={() => handleShipTrackingCardClick('shipconfirm')}
                >
                  <CardContent>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                      Ship Confirm - No Movement
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {shipTrackingData?.counts?.shipconfirm_nomoment || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <Typography variant="body2" color="text.secondary">
                No data available
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', boxShadow: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default ShipTracking;
